/**
 * Customer support / grievance tickets raised from the chat widget.
 *
 * Backend contract (PLANNED — endpoint to be implemented in FinMin_Backend;
 * this client is written against the agreed shape so the backend can match it):
 *
 *   POST /api/v1/customer/support/tickets
 *     body: { category, description, subject? }
 *     201:  SupportTicket
 *
 * Unlike the tax-KB chat (anonymous, plain fetch — see ./chat.ts), a ticket
 * must be attached to the signed-in customer, so this goes through the shared
 * authenticated `apiClient` and surfaces failures as `ApiError` per the repo
 * auth contract (CLAUDE.md §2).
 */
import { ApiError } from '@finmin/api-client';
import { apiClient } from '@/lib/api';

/** Issue buckets offered to the user as quick-reply chips during intake. */
export const SUPPORT_CATEGORIES = [
  { id: 'payments', label: 'Payments & billing' },
  { id: 'account', label: 'Login & account' },
  { id: 'service', label: 'Service status or delay' },
  { id: 'bug', label: 'Something is broken' },
  { id: 'other', label: 'Something else' },
] as const;

export type SupportCategoryId = (typeof SUPPORT_CATEGORIES)[number]['id'];

export function categoryLabel(id: SupportCategoryId): string {
  return SUPPORT_CATEGORIES.find((c) => c.id === id)?.label ?? 'Something else';
}

export interface SupportTicketRequest {
  category: SupportCategoryId;
  description: string;
  /** Optional short summary; backend may derive one if omitted. */
  subject?: string;
}

export interface SupportTicket {
  id: string;
  /** Human-facing reference, e.g. "TKT-4821". */
  reference: string;
  category: string;
  subject: string;
  description: string;
  /** e.g. "open". */
  status: string;
  created_at: string;
}

export async function createSupportTicket(
  payload: SupportTicketRequest,
): Promise<SupportTicket> {
  const { data } = await apiClient.post<SupportTicket>(
    '/api/v1/customer/support/tickets',
    payload,
  );
  return data;
}

/**
 * Maps a thrown error to a user-safe, conversational line for the widget.
 * `ApiError.status === 0` means a network/CORS failure (no response).
 */
export function supportErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const s = err.status;
    if (s === 0)
      return "I can't seem to reach our support desk right now. Check your connection and try again in a moment.";
    if (s === 429)
      return "I'm a bit swamped right now — give me a moment and try raising it again.";
    if (s === 401 || s === 403)
      return "I couldn't confirm your account just now. Please sign in again and retry.";
    if (s >= 500)
      return "I couldn't reach our support desk just now. Please try again in a moment.";
    return "I couldn't raise that ticket. Mind trying once more?";
  }
  return 'Something went wrong while raising your ticket. Please try again.';
}
